import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { axios } from 'api'
import { camelToSnake } from 'utils'
import moment from 'moment'

const statusMap = {
  not_started: 'Not Started',
  pending: 'Pending',
  processed: 'Processed',
  failed: 'Failed'
}

const requestedByMap = {
  cs_agent: 'CS Agent',
  user: 'User',
  auto_issue: 'Auto issue'
}

const Container = styled.div``

const StatusList = styled.ul`
  padding: 0 24px;
  li {
    text-transform: capitalize;
  }
  .status {
    text-transform: none;
  }
  .chip {
    padding: 0 4px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 16px;

    &.failed {
      background: #fff1f0;
      color: #a8071a;
      border: 1px solid #ffa39e;
    }
    &.processed {
      background: #e6ffed;
      color: #237804;
      border: 1px solid #b7eb8f;
    }
    &.pending {
      background: #fffbe6;
      color: #ad8b00;
      border: 1px solid #ffe58f;
    }
    &.not_started {
      background: #f5f5f5;
      color: #595959;
      border: 1px solid #d9d9d9;
    }
  }
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
`

const SuccessAlert = styled.div`
  margin: 16px 24px 0;
  padding: 6px 8px;
  border-radius: 4px;
  background: #e6ffed;
  color: #237804;
  border: 1px solid #b7eb8f;
  font-weight: 500;
  font-size: 14px;
  width: fit-content;
`

const RefundEntry = ({ username = '' }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState('')

  const getStatus = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await axios.post(
        '/all-free/refund_gc_selection_status',
        { username },
        { baseURL: '/admin/api' }
      )
      setData(camelToSnake(res.data))
    } catch (err) {
      setError('Failed to fetch refund status.')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [username])

  useEffect(() => {
    getStatus()
  }, [getStatus])

  const handleStatus = async (selection_type) => {
    let actionLabel =
      selection_type === 'gift_card' ? 'issue a gift voucher' : 'issue a refund'
    const confirmed = window.confirm(`Are you sure you want to ${actionLabel}?`)
    if (!confirmed) return
    setSubmitting(true)
    setSuccess('')
    try {
      const res = await axios.post(
        '/all-free/refund-selection',
        { username, selection_type },
        { baseURL: '/admin/api' }
      )
      if (res?.data.success) {
        setSuccess(
          selection_type === 'gift_card'
            ? 'Gift voucher request has been submitted and is pending processing.'
            : 'Refund request has been submitted and is pending processing.'
        )
        setTimeout(() => setSuccess(''), 4000)
      }
    } catch (err) {
      setError('Failed to process the action.')
      console.log(err)
    } finally {
      getStatus()
    }
    setSubmitting(false)
  }

  if (loading)
    return <div className='text-center text-secondary my-4'>Loading...</div>
  if (!data) return null

  if (data?.is_ios) {
    return (
      <Container>
        <StatusList>
          <li>
            The user was subscribed through Apple and is not eligible for a
            refund.
          </li>
        </StatusList>
      </Container>
    )
  }

  return (
    <Container>
      <StatusList>
        <li>
          Status:{' '}
          <span className={`chip ${data.status}`}>
            {statusMap[data.status] || data.status}
          </span>
        </li>
        {!Boolean(data?.refund_amount) && data?.status === 'not_started' && (
          <li className='status'>
            This user is ineligible to receive a refund/gift voucher.
          </li>
        )}
        {Number(data?.refund_amount) > 0 && !data?.gift_card_eligible && (
          <li className='status'>
            This user is ineligible to receive a gift voucher.
          </li>
        )}
        {data.selection_type && data.status !== 'not_started' && (
          <li>
            Selection Type:{' '}
            {data.selection_type === 'refund' ? 'Refund' : 'Gift Voucher'}
          </li>
        )}
        {data?.processed_time && (
          <li>
            Processed Time:{' '}
            {moment(data?.processed_time).local().format('DD-MMM-YYYY hh:mm A')}
          </li>
        )}
        {data.requested_by && (
          <li>
            Requested By:{' '}
            {requestedByMap[data.requested_by] || data.requested_by}
          </li>
        )}
        {data.cs_agent_name && (
          <li className='status'>CS Agent Name: {data.cs_agent_name}</li>
        )}
        {data.status_message && (
          <li className='status'>Status Message: {data.status_message}</li>
        )}
        {data.refund_error && (
          <li className='status'>Refund Error: {data.refund_error}</li>
        )}
        {data.last_updated && (
          <li>
            Last Updated:{' '}
            {moment(data?.last_updated).local().format('DD-MMM-YYYY hh:mm A')}
          </li>
        )}
      </StatusList>
      {error && <div className='text-center text-danger my-4'>{error}</div>}
      {success && <SuccessAlert>{success}</SuccessAlert>}
      {(data.gift_card_amount || data.refund_amount) && (
        <ButtonGroup>
          {data.gift_card_amount && (
            <button
              disabled={
                submitting || !['failed', 'not_started'].includes(data?.status)
              }
              className='btn btn-primary'
              onClick={() => handleStatus('gift_card')}
            >
              {submitting
                ? 'Processing...'
                : `Issue gift voucher of $${data.gift_card_amount}`}
            </button>
          )}
          {data.refund_amount && (
            <button
              disabled={
                submitting || !['failed', 'not_started'].includes(data?.status)
              }
              onClick={() => handleStatus('refund')}
              className='btn btn-outline-primary'
            >
              {submitting
                ? 'Processing...'
                : `Issue refund of $${data.refund_amount}`}
            </button>
          )}
        </ButtonGroup>
      )}
    </Container>
  )
}

export default RefundEntry
